import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { COLORS, SPACING } from '../styles/constants';

// Icons
import { FaChartBar, FaDatabase, FaCloud, FaCogs, FaShieldAlt } from 'react-icons/fa';

const ServicesPage = styled.div`
  color: ${COLORS.dark};
`;

const PageHeader = styled.section`
  background-color: ${COLORS.dark};
  color: ${COLORS.white};
  padding: ${SPACING.xxl} 0 ${SPACING.xxl};
  text-align: center;
`;

const HeaderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${SPACING.xl};
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: ${SPACING.lg};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background-color: ${COLORS.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.4rem;
  max-width: 900px;
  margin: ${SPACING.xl} auto 0;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: ${SPACING.xxl} ${SPACING.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  margin-bottom: ${SPACING.xl};
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background-color: ${COLORS.primary};
  }
`;

const ServicesList = styled.div`
  margin-top: ${SPACING.xxl};
`;

const ServiceItem = styled.div`
  display: flex;
  margin-bottom: ${SPACING.xxl};
  gap: ${SPACING.xl};
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: ${SPACING.xl};
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
  
  &:nth-child(even) {
    flex-direction: row-reverse;
  }
  
  @media (max-width: 768px) {
    flex-direction: column !important;
  }
`;

const ServiceIcon = styled.div`
  flex: 0 0 140px;
  height: 140px;
  background-color: ${COLORS.lightGrey};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.primary};
  font-size: 3rem;
  transition: all 0.3s ease;
  
  ${ServiceItem}:hover & {
    background-color: ${COLORS.primary};
    color: ${COLORS.white};
  }
  
  @media (max-width: 768px) {
    margin: 0 auto ${SPACING.lg};
  }
`;

const ServiceContent = styled.div`
  flex: 1;
`;

const ServiceTitle = styled.h3`
  font-size: 2.2rem;
  margin-bottom: ${SPACING.md};
  color: ${COLORS.dark};
  transition: color 0.3s ease;
  
  ${ServiceItem}:hover & {
    color: ${COLORS.primary};
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: ${SPACING.lg};
  color: ${COLORS.darkGrey};
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${SPACING.lg};
`;

const FeatureItem = styled.li`
  padding-left: 30px;
  position: relative;
  margin-bottom: ${SPACING.md};
  font-size: 1.1rem;
  line-height: 1.6;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${COLORS.primary};
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const CTASection = styled.section`
  background-color: ${COLORS.dark};
  padding: ${SPACING.xxl} 0;
  text-align: center;
  color: ${COLORS.white};
`;

const CTAContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 ${SPACING.xl};
`;

const CTATitle = styled.h2`
  font-size: 2.8rem;
  margin-bottom: ${SPACING.lg};
`;

const CTAText = styled.p`
  font-size: 1.3rem;
  margin-bottom: ${SPACING.xl};
  line-height: 1.6;
`;

const CTAButton = styled(Link)`
  background-color: ${COLORS.primary};
  color: ${COLORS.white};
  padding: ${SPACING.md} ${SPACING.xxl};
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.3rem;
  display: inline-block;
  transition: all 0.3s ease;
  border: 2px solid ${COLORS.primary};
  
  &:hover {
    background-color: transparent;
    border-color: ${COLORS.white};
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
`;

const Services = () => {
  const services = [
    {
      icon: <FaChartBar size={56} />,
      title: "Advanced & Actionable Insights",
      description: "Transform your data into clear, actionable business intelligence with our comprehensive reporting and dashboarding solutions.",
      features: [
        "Interactive dashboards and visualizations",
        "Self-service analytics capabilities",
        "Embedded analytics for applications",
        "Mobile-optimized reporting solutions",
        "Real-time data monitoring and alerts"
      ]
    },
    {
      icon: <FaDatabase size={56} />,
      title: "Data Engineering (ELT/ETL)",
      description: "Build scalable, reliable data pipelines that efficiently extract, transform, and load your data for seamless analysis and insights.",
      features: [
        "Custom ETL/ELT pipeline development",
        "Data quality monitoring and validation",
        "Batch and stream processing solutions",
        "Performance optimization for data workflows",
        "Integration with existing data infrastructure"
      ]
    },
    {
      icon: <FaCogs size={56} />,
      title: "Data Science and Machine Learning",
      description: "Harness the power of advanced algorithms and predictive models to uncover patterns, generate insights, and drive data-informed innovation.",
      features: [
        "Predictive analytics and forecasting",
        "Customer segmentation and behavior analysis",
        "Recommendation engines and personalization",
        "Anomaly detection and fraud prevention",
        "Natural language processing and text analytics"
      ]
    },
    {
      icon: <FaDatabase size={56} />,
      title: "System to System Integration",
      description: "Connect your diverse systems and platforms to create a unified, coherent data ecosystem that eliminates silos and enhances collaboration.",
      features: [
        "API development and integration",
        "Middleware implementation",
        "Legacy system integration",
        "Real-time data synchronization",
        "Event-driven architecture development"
      ]
    },
    {
      icon: <FaShieldAlt size={56} />,
      title: "Data Governance",
      description: "Establish robust frameworks that ensure your data assets are secure, compliant, trusted, and delivering maximum value to your organization.",
      features: [
        "Data catalog and metadata management",
        "Data lineage and impact analysis",
        "Privacy and compliance solutions",
        "Master data management",
        "Data quality frameworks and monitoring"
      ]
    },
    {
      icon: <FaCloud size={56} />,
      title: "Cloud Strategy and Delivery",
      description: "Design and implement cloud solutions that scale with your business, offering flexibility, performance, and cost optimization.",
      features: [
        "Cloud architecture design",
        "Migration strategy and implementation",
        "Multi-cloud and hybrid cloud solutions",
        "Cloud cost optimization",
        "DevOps and CI/CD pipeline integration"
      ]
    }
  ];

  return (
    <ServicesPage>
      <PageHeader>
        <HeaderContainer>
          <PageTitle>Tailored Solutions to Empower Your Data-Driven Journey</PageTitle>
          <PageSubtitle>
            Explore our suite of services designed to transform your data landscape and unlock new 
            possibilities for growth, efficiency, and innovation.
          </PageSubtitle>
        </HeaderContainer>
      </PageHeader>
      
      <Section>
        <SectionTitle>Our Services</SectionTitle>
        <ServicesList>
          {services.map((service, index) => (
            <ServiceItem key={index}>
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceContent>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <FeaturesList>
                  {service.features.map((feature, i) => (
                    <FeatureItem key={i}>{feature}</FeatureItem>
                  ))}
                </FeaturesList>
              </ServiceContent>
            </ServiceItem>
          ))}
        </ServicesList>
      </Section>
      
      <CTASection>
        <CTAContainer>
          <CTATitle>Ready to Start Your Data Transformation?</CTATitle>
          <CTAText>
            Our team of experts is ready to help you navigate your unique data challenges and build a 
            tailored solution that drives real business value.
          </CTAText>
          <CTAButton to="/contact">Schedule a Consultation</CTAButton>
        </CTAContainer>
      </CTASection>
    </ServicesPage>
  );
};

export default Services; 